import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Plus, Check } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface CalendarContentProps {
  animationDelay?: number;
}

const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const times = ['8 AM', '9 AM', '10 AM', '11 AM', '12 PM', '1 PM', '2 PM'];

const events = [
  { day: 1, time: 2, duration: 1, title: 'Strategy Call', color: '#3B82F6' },
  { day: 3, time: 3, duration: 2, title: 'Masterclass', color: '#8B5CF6' },
  { day: 4, time: 1, duration: 1, title: 'Team Sync', color: '#10B981' },
];

const CalendarContent: React.FC<CalendarContentProps> = ({ animationDelay = 0 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [eventConfirmed, setEventConfirmed] = useState(false);

  useEffect(() => {
    const showTimer = setTimeout(() => {
      setShowNewEvent(true);
    }, animationDelay + 1500);

    const confirmTimer = setTimeout(() => {
      setEventConfirmed(true);
    }, animationDelay + 2200);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(confirmTimer);
    };
  }, [animationDelay]);

  return (
    <div 
      className="flex-1 flex flex-col overflow-hidden"
      style={{ 
        backgroundColor: isDark ? '#1A1A1A' : '#F5F5F5',
        animation: `fadeIn 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between px-3 py-2 border-b"
        style={{ 
          borderColor: isDark ? '#333333' : '#E5E5E5',
          backgroundColor: isDark ? '#222222' : '#FFFFFF',
          animation: `fadeIn 0.3s ease-out ${animationDelay + 100}ms both`
        }}
      >
        <div className="flex items-center gap-2">
          <button 
            className="p-0.5 rounded"
            style={{ backgroundColor: isDark ? '#333333' : '#F0F0F0' }}
          >
            <ChevronLeft className="w-3 h-3" style={{ color: isDark ? '#888888' : '#666666' }} />
          </button>
          <span className="text-[9px] font-semibold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
            Jan 12 - 18, 2026
          </span>
          <button 
            className="p-0.5 rounded"
            style={{ backgroundColor: isDark ? '#333333' : '#F0F0F0' }}
          >
            <ChevronRight className="w-3 h-3" style={{ color: isDark ? '#888888' : '#666666' }} />
          </button>
        </div>
        <div className="flex items-center gap-1">
          <span 
            className="text-[7px] px-1.5 py-0.5 rounded"
            style={{ 
              backgroundColor: isDark ? '#3B82F620' : '#3B82F610',
              color: '#3B82F6'
            }}
          >
            Week View
          </span>
          <div 
            className="flex items-center gap-0.5 text-[7px] px-1.5 py-0.5 rounded"
            style={{ 
              backgroundColor: isDark ? '#10B98120' : '#10B98110',
              color: '#10B981'
            }}
          >
            <Plus className="w-2 h-2" />
            Book
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div 
        className="flex-1 flex overflow-hidden"
        style={{ animation: `fadeIn 0.4s ease-out ${animationDelay + 200}ms both` }}
      >
        {/* Time Column */}
        <div 
          className="flex flex-col border-r"
          style={{ 
            width: '28px',
            borderColor: isDark ? '#333333' : '#E5E5E5',
            backgroundColor: isDark ? '#222222' : '#FFFFFF'
          }}
        >
          <div className="h-5 border-b" style={{ borderColor: isDark ? '#333333' : '#E5E5E5' }} />
          {times.map((time, i) => (
            <div 
              key={i}
              className="flex-1 flex items-start justify-end pr-1 pt-0.5 text-[5px]"
              style={{ 
                color: isDark ? '#666666' : '#999999',
                borderBottom: `1px solid ${isDark ? '#2A2A2A' : '#F0F0F0'}`,
                animation: `fadeIn 0.2s ease-out ${animationDelay + 250 + (i * 30)}ms both`
              }}
            >
              {time}
            </div>
          ))}
        </div>

        {/* Days Grid */}
        <div className="flex-1 flex">
          {days.map((day, dayIndex) => (
            <div 
              key={dayIndex}
              className="flex-1 flex flex-col border-r last:border-r-0"
              style={{ 
                borderColor: isDark ? '#2A2A2A' : '#F0F0F0',
                animation: `fadeIn 0.3s ease-out ${animationDelay + 200 + (dayIndex * 50)}ms both`
              }}
            >
              {/* Day Header */}
              <div 
                className="h-5 flex flex-col items-center justify-center border-b"
                style={{ 
                  borderColor: isDark ? '#333333' : '#E5E5E5',
                  backgroundColor: dayIndex === 1 ? (isDark ? '#3B82F620' : '#3B82F610') : (isDark ? '#222222' : '#FFFFFF')
                }}
              >
                <span className="text-[5px]" style={{ color: isDark ? '#666666' : '#999999' }}>
                  {day}
                </span>
                <span 
                  className="text-[7px] font-medium"
                  style={{ color: dayIndex === 1 ? '#3B82F6' : (isDark ? '#FFFFFF' : '#111111') }}
                >
                  {12 + dayIndex}
                </span>
              </div>

              {/* Time Slots */}
              {times.map((_, timeIndex) => {
                const event = events.find(e => e.day === dayIndex && e.time === timeIndex);
                const isNewEventSlot = dayIndex === 5 && timeIndex === 2;
                
                return (
                  <div 
                    key={timeIndex}
                    className="flex-1 border-b relative"
                    style={{ 
                      borderColor: isDark ? '#2A2A2A' : '#F0F0F0',
                      backgroundColor: isDark ? '#1A1A1A' : '#F9F9F9'
                    }}
                  >
                    {event && (
                      <div 
                        className="absolute inset-x-0.5 top-0.5 rounded p-0.5"
                        style={{ 
                          backgroundColor: `${event.color}20`,
                          borderLeft: `2px solid ${event.color}`,
                          height: event.duration > 1 ? 'calc(200% - 2px)' : 'calc(100% - 4px)',
                          animation: `slideInFromLeft 0.4s ease-out ${animationDelay + 600 + (dayIndex * 100)}ms both`,
                          zIndex: 10
                        }}
                      >
                        <span className="text-[5px] font-medium" style={{ color: event.color }}>
                          {event.title}
                        </span>
                      </div>
                    )}

                    {/* New Event Animation */}
                    {isNewEventSlot && showNewEvent && (
                      <div 
                        className="absolute inset-x-0.5 top-0.5 rounded p-0.5"
                        style={{ 
                          backgroundColor: eventConfirmed ? '#F59E0B20' : (isDark ? '#333333' : '#E5E5E5'),
                          borderLeft: eventConfirmed ? '2px solid #F59E0B' : '2px dashed #F59E0B',
                          height: 'calc(100% - 4px)',
                          transition: 'all 0.3s ease',
                          zIndex: 10
                        }}
                      >
                        <div className="flex items-center gap-0.5">
                          {eventConfirmed && (
                            <Check 
                              className="w-2 h-2" 
                              style={{ 
                                color: '#F59E0B',
                                animation: 'scaleIn 0.3s ease-out'
                              }} 
                            />
                          )}
                          <span 
                            className="text-[5px] font-medium" 
                            style={{ color: '#F59E0B' }}
                          >
                            {eventConfirmed ? 'Confirmed!' : 'New booking...'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Status */}
      <div 
        className="flex items-center justify-between px-3 py-1.5 border-t"
        style={{ 
          borderColor: isDark ? '#333333' : '#E5E5E5',
          backgroundColor: isDark ? '#222222' : '#FFFFFF',
          animation: `fadeIn 0.3s ease-out ${animationDelay + 500}ms both`
        }}
      >
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded" style={{ backgroundColor: '#3B82F620', borderLeft: '2px solid #3B82F6' }} />
            <span className="text-[6px]" style={{ color: isDark ? '#888888' : '#666666' }}>Calls</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded" style={{ backgroundColor: '#8B5CF620', borderLeft: '2px solid #8B5CF6' }} />
            <span className="text-[6px]" style={{ color: isDark ? '#888888' : '#666666' }}>Events</span>
          </div>
        </div>
        <span className="text-[7px]" style={{ color: isDark ? '#888888' : '#666666' }}>
          <span style={{ color: '#10B981' }}>8</span> bookings this week
        </span>
      </div>

      <style>{`
        @keyframes slideInFromLeft {
          0% { transform: translateX(-100%); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default CalendarContent;
